import * as React from 'react';
import { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';

import { DEFAULT_PRICING_MODEL } from './constants';
import { CountryCode, Option, TangoTeamProduct } from './types';
import { getProductPricingAndNutrientInfo } from '../api/getProductPricingAndNutrientInfo';
import { Dropdown } from '../component/Dropdown';
import { TableProductInfo } from '../component/TableProductInfo';
import { AncillaryProductInfoHeaders } from '../tableHeaders';
import {
  BOTTOM_TOAST,
  createRowClickHandler,
  generateErrorMsg,
  getCountryFromCurrency,
} from '../utils';

export const AncillaryProductInfo = () => {
  const [ancillaryProduct, setAncillaryProduct] = useState<TangoTeamProduct[]>([]);
  const [loading, setLoading] = useState(true);

  const [selectedPricingModel, setSelectedPricingModel] = useState<Option>({
    label: DEFAULT_PRICING_MODEL,
    value: DEFAULT_PRICING_MODEL,
  });
  const [highlightedRows, setHighlightedRows] = useState<Set<number>>(new Set());
  const [lastSelectedRow, setLastSelectedRow] = useState<number | null>(null);

  useEffect(() => {
    getProductPricingAndNutrientInfo({ meal_type: 'ANCILLARY' })
      .then((res) => {
        setAncillaryProduct(res.data.reverse());
      })
      .catch((err) => {
        toast.error(generateErrorMsg(err), BOTTOM_TOAST);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const { pricingModelOptions } = useMemo(() => {
    if (ancillaryProduct.length === 0) {
      return { pricingModelOptions: [] };
    }

    const pricingModelSet = new Set<string>();

    for (const product of ancillaryProduct) {
      for (const price of Object.values(product.prices)) {
        if (price?.pricing_model_name) {
          pricingModelSet.add(String(price.pricing_model_name));
        }
      }
    }
    const pricingModelOptions = Array.from(pricingModelSet).map((v) => ({ label: v, value: v }));

    return { pricingModelOptions };
  }, [ancillaryProduct]);

  const firstHeaderGroups = [
    { label: '', colSpan: 5, className: 'bg-slate50' },
    { label: 'Pricing', colSpan: 1, className: 'bg-green-200 font-semibold' },
    { label: '', colSpan: 1, className: 'bg-purple-200 font-semibold' },
  ];

  const handleRowClick = createRowClickHandler(
    setHighlightedRows,
    setLastSelectedRow,
    lastSelectedRow,
    highlightedRows
  );

  const transformProductToTableRow = (product: TangoTeamProduct) => {
    const prices = product?.prices?.[selectedPricingModel.value];
    const currency = prices?.currency ? `${prices.currency} $` : '';
    const country = getCountryFromCurrency(prices?.currency);

    return {
      Code: product.code,
      Description: product.description_bark,
      Type: product.type,
      Recurring: product.recurring.toString(),
      Status:
        country === CountryCode.CA
          ? product.status_ca
          : country === CountryCode.US
          ? product.status_us
          : '--',
      'Price (per Product Code)': `${currency}${(prices?.price ?? 0.0).toFixed(2)}`,
      'Product contents': product.recipe_data_point,
    };
  };

  return (
    <div className="h-screen flex flex-col">
      <div className="p-10 flex-shrink-0">
        <h1>Ancillary Product Info</h1>
      </div>

      {!loading && (
        <div className="px-10 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg border">
            <h3 className="text-lg font-semibold mb-4">Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div>
                <label className="block text-sm font-medium mb-2">Pricing Model</label>
                <Dropdown
                  options={pricingModelOptions}
                  selectedOption={selectedPricingModel}
                  setSelectedOption={setSelectedPricingModel}
                  defaultText="Select Pricing Model"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          <span className="ml-2">Loading...</span>
        </div>
      ) : (
        <div className="px-10 flex-1 flex flex-col">
          <div className="flex-1 border border-gray-300 rounded-lg">
            <TableProductInfo
              header={AncillaryProductInfoHeaders}
              data={ancillaryProduct.map((product: TangoTeamProduct) =>
                transformProductToTableRow(product)
              )}
              topHeaderGroups={firstHeaderGroups}
              highlightedRows={highlightedRows}
              onRowClick={handleRowClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};
